import React, { useState } from 'react';
import useMedicalProfile from '../../../../../hooks/api/useMedicalProfile';
import { getIntl } from '../../../../../utils/intl';
import useSetHeaderTitle from '../../../../../hooks/useSetHeaderTitle';
import LabelValueDisplay, { LabelValueItem } from '../../../../v2BaseComponents/LabelValueDisplay';
import getAge from '../../../../../utils/getAge';
import addressToString from '../../../../../utils/addressToString';
import {
    EditProfileTab,
    ProfilesTabParamList,
    useProfilesTabNavigation,
} from '../../../../../utils/appNavigationNames';
import { EmergencyContactDetails } from '../../../../../primary-record-types/apiTypes/medicalProfileService';
import getName from '../../../../../utils/getName';
import genderSexToString from '../../../../../utils/genderSexToString';
import { useHasPermission, useIsProfileOwner } from '../../../../v2BaseComponents/Permissions/hooks';
import MedicalProfileCards from './MedicalProfileCards';
import { TabView } from '../../../../v2BaseComponents/TabView';
import getDateString from '../../../../../utils/getDateString';
import { RouteProp } from '@react-navigation/native';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import MainContentContainer from '../../../../v2BaseComponents/MainContentContainer';
import TitledSection from '../../../../v2BaseComponents/TitledSection';
import Stack, { ContentStack } from '../../../../v2BaseComponents/Stack';
import { LoadableAvatar, getFallbackFromName } from '../../../../v2BaseComponents/Avatar';
import urls from '../../../../../utils/urls';
import { useDeleteProfileButton } from '../../../../../hooks/useDeleteButtonModal';
import useRecordEvent from '../../../../../hooks/useRecordEvent';
import AdvancedDirectives from './AdvancedDirectives';

const intl = getIntl('profileScree.editProfile', {
    header: `Profile Information`,
    personalInformation: 'Personal Information',
    fullLegalName: 'Full Legal Name',
    dob: 'Date of Birth',
    preferredName: 'Preferred Name',
    gender: 'Gender',
    sex: 'Sex assigned at birth',
    phone: 'Phone',
    email: 'Email',
    address: 'Address',
    emergencyContact: 'Emergency Contacts',
    information: 'Information',
    idCards: 'ID Cards',
    edit: 'Edit',
    addOrEdit: 'Add or Edit',
    editProfileInfo: 'Edit',
    deleteProfile: 'Delete Profile',
    advancedDirectives: 'Advanced Directives',
});

export type EditProfileScreenOwnProps = {};

export type EditProfileScreenProps = EditProfileScreenOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'edit'>;
};

const EditProfileScreen: React.FC<EditProfileScreenProps> = (props) => {
    const [activeTab, setActiveTab] = useState<EditProfileTab>(props.route.params.defaultTab || 'information');
    const navigation = useProfilesTabNavigation();
    const { profileId } = props.route.params;
    const { profile, isLoadingProfile } = useMedicalProfile({ medicalProfileId: profileId });
    const canViewCards = useHasPermission(profileId, 'idsAndInfo', 'view');
    const canViewAdvancedDirectives = canViewCards;
    const hasEditPermissions = useHasPermission(profileId, 'idsAndInfo', 'edit');
    const recordEvent = useRecordEvent();
    const isProfileOwner = useIsProfileOwner(profileId);
    const deleteProfileButton = useDeleteProfileButton({
        profileId,
        onDeleteSuccess: () => {
            recordEvent('deleteProfile');
            navigation.navigate('root');
        },
    });
    useSetHeaderTitle(intl('header'));

    const getContactLabelValues = (): LabelValueItem[] => {
        const getDob = () => {
            if (!profile?.contactDetails.dob) return '';
            const age = getAge(profile?.contactDetails.dob);
            const date = getDateString(profile?.contactDetails.dob, 'date');
            return age ? `${date} (${age})` : date;
        };

        return [
            {
                label: intl('fullLegalName'),
                value: `${profile?.contactDetails.firstName || ''} ${profile?.contactDetails.lastName || ''}`,
            },
            {
                label: intl('dob'),
                value: getDob(),
            },
            {
                label: intl('preferredName'),
                value: profile?.contactDetails.preferredFirstName || '',
            },
            {
                label: intl('gender'),
                value: `${genderSexToString(profile?.contactDetails.gender) || ''}`,
            },
            {
                label: intl('sex'),
                value: `${genderSexToString(profile?.contactDetails.sex) || ''}`,
            },
            {
                label: intl('phone'),
                value: profile?.contactDetails.phoneNumber || '',
                linkType: 'phone',
            },
            {
                label: intl('email'),
                value: profile?.contactDetails.email || '',
                linkType: 'email',
            },
            {
                label: intl('address'),
                value: addressToString({
                    address1: profile?.contactDetails.address1,
                    address2: profile?.contactDetails.address2,
                    city: profile?.contactDetails.city,
                    state: profile?.contactDetails.state,
                    zipCode: profile?.contactDetails.zipCode,
                }),
                linkType: 'address',
            },
        ];
    };

    const getEmergencyContactLabelValues = (): LabelValueItem[] => {
        const getLabel = (contact: EmergencyContactDetails) => {
            const name = getName({ firstName: contact.firstName, lastName: contact.lastName });
            return [name, contact.relationship].filter(Boolean).join('\n');
        };

        const getValue = (contact: EmergencyContactDetails) => {
            return [contact.phoneNumber, contact.email].filter(Boolean).join('\n');
        };

        // @ts-expect-error TS18047
        return profile?.emergencyContactDetails
            .map((contact) => {
                return {
                    label: getLabel(contact),
                    value: getValue(contact),
                };
            })
            .filter((_) => !!_.value);
    };

    const renderIDCards = () => <MedicalProfileCards profileId={profileId} />;

    const renderInformation = () => (
        <ActionButtonLayout
            whiteBackground
            scrollable
            actions={[
                {
                    icon: 'Edit2',
                    title: intl('editProfileInfo'),
                    onPress: () => {
                        navigation.navigate('contact-details', {
                            // @ts-expect-error TS18047
                            profileId: profile?.medicalProfileId,
                        });
                    },
                    show: hasEditPermissions,
                },
                {
                    icon: 'Edit2',
                    title: intl('emergencyContact'),
                    onPress: () => {
                        navigation.navigate('emergency-contacts', {
                            // @ts-expect-error TS18047
                            profileId: profile?.medicalProfileId,
                        });
                    },
                    show: hasEditPermissions,
                },
                {
                    icon: 'Trash2',
                    title: intl('deleteProfile'),
                    onPress: deleteProfileButton.onPress,
                    show: isProfileOwner,
                    isDisabled: deleteProfileButton.isDeletingProfile,
                    displayInMoreMenu: true,
                    testID: 'delete-profile-prompt-button',
                },
            ]}
        >
            <MainContentContainer>
                <ContentStack>
                    <Stack horizontal fullWidth justify="center">
                        <LoadableAvatar
                            avatarUrl={urls.api.medicalProfiles.avatar(profileId)}
                            size="xxl"
                            fallbackName={getFallbackFromName(
                                profile?.contactDetails.firstName,
                                profile?.contactDetails.lastName,
                            )}
                        />
                    </Stack>
                    <TitledSection title={intl('personalInformation')}>
                        <LabelValueDisplay items={getContactLabelValues()} />
                    </TitledSection>
                    {getEmergencyContactLabelValues()?.length ? (
                        <TitledSection title={intl('emergencyContact')}>
                            <LabelValueDisplay items={getEmergencyContactLabelValues()} />
                        </TitledSection>
                    ) : null}
                </ContentStack>
            </MainContentContainer>
        </ActionButtonLayout>
    );

    return (
        <AnimateLoadedContent isLoading={isLoadingProfile}>
            {() => (
                <>
                    <TabView
                        selectedTab={activeTab}
                        tabs={[
                            {
                                title: intl('information'),
                                value: 'information',
                                renderTabContent: renderInformation,
                            },
                            {
                                title: intl('idCards'),
                                value: 'idCards',
                                isDisabled: !canViewCards,
                                renderTabContent: renderIDCards,
                            },
                            {
                                title: intl('advancedDirectives'),
                                value: 'advancedDirectives',
                                isDisabled: !canViewAdvancedDirectives,
                                renderTabContent: () => <AdvancedDirectives profileId={profileId} />,
                            },
                        ]}
                        onChange={(value) => setActiveTab(value as EditProfileTab)}
                    />
                    {deleteProfileButton.modal}
                </>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(EditProfileScreen);
